<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';

import DeleteDialog from 'dashboard/components-next/pilot/pageComponents/DeleteDialog.vue';
import BulkDeleteDialog from 'dashboard/components-next/pilot/pageComponents/BulkDeleteDialog.vue';
import CreateKnowledgeBaseDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/CreateKnowledgeBaseDialog.vue';
import EditKnowledgeBaseDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/EditKnowledgeBaseDialog.vue';
import KnowledgeBaseCard from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/KnowledgeBaseCard.vue';
import KnowledgeBasesListControls from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/KnowledgeBasesListControls.vue';
import KnowledgeBasePageEmptyState from 'dashboard/components-next/pilot/pageComponents/emptyStates/KnowledgeBasePageEmptyState.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';
import KnowledgeBasesFilter from 'dashboard/components-next/filter/KnowledgeBasesFilter.vue';
import BulkSelectBar from 'dashboard/components-next/pilot/assistant/BulkSelectBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// 导入 SVG 图片
import documentPopoverLight from 'assets/images/dashboard/pilot/document-popover-light.svg';
import documentPopoverDark from 'assets/images/dashboard/pilot/document-popover-dark.svg';

// 新增组件
import KnowledgeBasesSidebar from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/KnowledgeBasesSidebar.vue';
import SelectKnowledgeBaseDialog from 'dashboard/components-next/pilot/pageComponents/knowledgeBase/SelectKnowledgeBaseDialog.vue';

const { isOnChatwootCloud, account } = useAccount();
const { t } = useI18n();
const store = useStore();
const router = useRouter();

const uiFlags = useMapGetter('pilotKnowledgeBases/getUIFlags');
const knowledgeBases = useMapGetter('pilotKnowledgeBases/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const knowledgeBasesMeta = useMapGetter('pilotKnowledgeBases/getMeta');

// 为 meta 提供默认值
const knowledgeBasesMetaSafe = computed(
  () =>
    knowledgeBasesMeta.value || {
      totalCount: 0,
      page: 1,
      pageSize: 25,
    }
);

const knowledgeBasesArray = computed(() => knowledgeBases.value || []);
const isEmpty = computed(() => !knowledgeBasesArray.value.length);
const isSearching = computed(() => searchValue.value.trim() !== '');
const isFiltering = computed(() => hasAppliedFilters.value);
const showEmptyState = computed(
  () => isEmpty.value && !isSearching.value && !isFiltering.value
);
const showNoResults = computed(
  () => isEmpty.value && (isSearching.value || isFiltering.value)
);

// 搜索和排序状态
const searchValue = ref('');
const sortBy = ref('created_at');
const sortOrder = ref('-'); // '' 表示升序，'-' 表示降序

// 筛选状态
const isFilterOpen = ref(false);
const appliedFilters = ref([]);
const filterQuery = ref([]);

let filterIdCounter = 0;
// 默认筛选条件
const DEFAULT_FILTER = {
  attributeKey: 'name',
  filterOperator: 'contains',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

// 当前打开的菜单 ID
const openMenuId = ref(null);

// 批量选择
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

// 侧边栏状态
const sidebarCollapsed = ref(false);
const activeSidebarItem = ref('knowledgeBases');
const selectKbDialogRef = ref(null);
const pendingAction = ref(null);

// 移动端菜单状态
const isMobileMenuOpen = ref(false);

// 是否为移动端
const isMobile = ref(false);
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};
if (typeof window !== 'undefined') {
  checkMobile();
  window.addEventListener('resize', checkMobile);
}

// 处理菜单打开
const handleMenuOpen = id => {
  openMenuId.value = id;
};

// 处理菜单关闭
const handleMenuClose = () => {
  openMenuId.value = null;
};

// 批量选择相关
const buildSelectedCountLabel = computed(() => {
  const count = knowledgeBasesArray.value.length;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('PILOT.KNOWLEDGE_BASES.UNSELECT_ALL', { count })
    : t('PILOT.KNOWLEDGE_BASES.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('PILOT.KNOWLEDGE_BASES.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleCardHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const handleCardSelect = (id, isSelected) => {
  const selected = new Set(bulkSelectedIds.value);
  if (isSelected) {
    selected.add(id);
  } else {
    selected.delete(id);
  }
  bulkSelectedIds.value = selected;
};

// 防抖搜索 - 优化避免闪烁
let searchDebounceTimer = null;
const handleSearchChange = value => {
  searchValue.value = value;
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    fetchKnowledgeBases(1);
  }, 500); // 增加到500ms，减少请求频率
};

const handleSortChange = ({ sort, order }) => {
  sortBy.value = sort;
  sortOrder.value = order;
  fetchKnowledgeBases(1);
};

// 筛选相关函数
const handleToggleFilter = () => {
  isFilterOpen.value = !isFilterOpen.value;
  if (isFilterOpen.value && filterQuery.value.length === 0) {
    filterQuery.value = [{ ...DEFAULT_FILTER, id: filterIdCounter++ }];
  }
};

const handleCloseFilter = () => {
  isFilterOpen.value = false;
};

const handleClearFilters = () => {
  appliedFilters.value = [];
  filterQuery.value = [{ ...DEFAULT_FILTER, id: filterIdCounter++ }];
  fetchKnowledgeBases(1);
};

const handleApplyFilter = filters => {
  appliedFilters.value = filters;
  fetchKnowledgeBases(1);
  isFilterOpen.value = false;
};

const hasAppliedFilters = computed(() => appliedFilters.value.length > 0);

const selectedKnowledgeBase = ref(null);
const deleteKnowledgeBaseDialog = ref(null);
const bulkDeleteDialog = ref(null);
const showCreateDialog = ref(false);
const createKnowledgeBaseDialog = ref(null);
const editKnowledgeBaseDialog = ref(null);

const handleDelete = () => {
  deleteKnowledgeBaseDialog.value.dialogRef.open();
};

const handleCreateKnowledgeBase = () => {
  showCreateDialog.value = true;
  nextTick(() => createKnowledgeBaseDialog.value.open());
};

const handleCreateDialogClose = () => {
  showCreateDialog.value = false;
};

const handleCreateSuccess = () => {
  fetchKnowledgeBases();
  // 如果是从选择弹窗创建的，创建成功后重新打开选择弹窗
  if (reopenSelectDialog.value) {
    reopenSelectDialog.value = false;
    nextTick(() => {
      selectKbDialogRef.value?.open();
    });
  }
};

const reopenSelectDialog = ref(false);

const handleCreateFromSelectDialog = () => {
  reopenSelectDialog.value = true;
  handleCreateKnowledgeBase();
};

const handleEditSuccess = () => {
  // store 会自动更新本地数据，不需要刷新整个列表
};

const handleAction = async ({ action, id }) => {
  selectedKnowledgeBase.value = knowledgeBasesArray.value.find(
    lib => id === lib.id
  );

  if (!selectedKnowledgeBase.value) return;

  switch (action) {
    case 'edit':
      nextTick(() => {
        editKnowledgeBaseDialog.value.open();
      });
      break;

    case 'publish':
      await handleStatusChange(id, 'published');
      break;

    case 'unpublish':
      await handleStatusChange(id, 'draft');
      break;

    case 'delete':
      nextTick(() => {
        handleDelete();
      });
      break;
  }
};

// 处理状态变更（发布/下架）
const handleStatusChange = async (id, newStatus) => {
  try {
    await store.dispatch('pilotKnowledgeBases/update', {
      id,
      data: { status: newStatus },
    });

    const message =
      newStatus === 'published'
        ? t('PILOT.KNOWLEDGE_BASES.PUBLISH.SUCCESS_MESSAGE')
        : t('PILOT.KNOWLEDGE_BASES.UNPUBLISH.SUCCESS_MESSAGE');
    useAlert(message);
    // store 会自动更新本地数据，不需要刷新整个列表
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.KNOWLEDGE_BASES.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// 批量操作相关
const handleBulkPublish = async () => {
  try {
    const ids = Array.from(bulkSelectedIds.value);
    await Promise.all(
      ids.map(id =>
        store.dispatch('pilotKnowledgeBases/update', {
          id,
          data: { status: 'published' },
        })
      )
    );
    useAlert(
      t('PILOT.KNOWLEDGE_BASES.BULK_PUBLISH.SUCCESS_MESSAGE', {
        count: ids.length,
      })
    );
    bulkSelectedIds.value = new Set();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.KNOWLEDGE_BASES.BULK_PUBLISH.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleBulkUnpublish = async () => {
  try {
    const ids = Array.from(bulkSelectedIds.value);
    await Promise.all(
      ids.map(id =>
        store.dispatch('pilotKnowledgeBases/update', {
          id,
          data: { status: 'draft' },
        })
      )
    );
    useAlert(
      t('PILOT.KNOWLEDGE_BASES.BULK_UNPUBLISH.SUCCESS_MESSAGE', {
        count: ids.length,
      })
    );
    bulkSelectedIds.value = new Set();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.KNOWLEDGE_BASES.BULK_UNPUBLISH.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleBulkDelete = () => {
  bulkDeleteDialog.value.dialogRef.open();
};

const onBulkDeleteSuccess = () => {
  const hasNoKnowledgeBasesLeft = knowledgeBasesArray.value.length === 0;
  const currentPage = knowledgeBasesMetaSafe.value.page;

  if (hasNoKnowledgeBasesLeft) {
    if (currentPage > 1) {
      fetchKnowledgeBases(currentPage - 1);
    } else {
      fetchKnowledgeBases(1);
    }
  }
  bulkSelectedIds.value = new Set();
};

const fetchKnowledgeBases = (page = 1) => {
  // 如果有筛选条件，使用 filter API
  if (hasAppliedFilters.value) {
    const sortAttr =
      sortOrder.value === '-' ? `-${sortBy.value}` : sortBy.value;
    // 转换驼峰式键名到下划线式
    const snakeCaseFilters = useSnakeCase(
      JSON.parse(JSON.stringify(appliedFilters.value))
    );
    const queryPayload = filterQueryGenerator(snakeCaseFilters);
    store.dispatch('pilotKnowledgeBases/filter', {
      page,
      sortAttr,
      queryPayload,
      search: searchValue.value, // 传递搜索参数
    });
    return;
  }

  // 否则使用常规的 get API
  const params = { page };

  if (searchValue.value) {
    params.search = searchValue.value;
  }

  // Chatwoot 排序格式：'-created_at' 表示降序，'created_at' 表示升序
  if (sortBy.value) {
    const sortPrefix = sortOrder.value === '-' ? '-' : '';
    params.sort = `${sortPrefix}${sortBy.value}`;
  }

  store.dispatch('pilotKnowledgeBases/get', params);
};

const onPageChange = page => fetchKnowledgeBases(page);

const onDeleteSuccess = () => {
  if (
    knowledgeBasesArray.value.length === 0 &&
    knowledgeBasesMetaSafe.value.page > 1
  ) {
    onPageChange(knowledgeBasesMetaSafe.value.page - 1);
  }
};

// 侧边栏相关
const handleSidebarSelect = item => {
  if (item.id === 'knowledgeBases') {
    activeSidebarItem.value = item.id;
    isMobileMenuOpen.value = false;
    return;
  }

  // 上传文档：弹窗选择知识库后跳转
  if (item.id === 'uploadDocument') {
    isMobileMenuOpen.value = false;
    pendingAction.value = 'uploadDocument';
    selectKbDialogRef.value?.open();
    return;
  }

  // 抓取网页：弹窗选择知识库后跳转
  if (item.id === 'crawlWebpage') {
    isMobileMenuOpen.value = false;
    pendingAction.value = 'crawlWebpage';
    selectKbDialogRef.value?.open();
    return;
  }

  // 抓取公众号直接跳转全局公众号抓取页面
  if (item.id === 'crawlWechat') {
    isMobileMenuOpen.value = false;
    router.push({
      name: 'pilot_global_documents_wechat_crawl',
    });
    return;
  }

  // 批量导入FAQ：弹窗选择知识库后跳转
  if (item.id === 'importFaq') {
    isMobileMenuOpen.value = false;
    pendingAction.value = 'importFaq';
    selectKbDialogRef.value?.open();
    return;
  }

  // 添加问答：弹窗选择知识库后跳转
  if (item.id === 'addFaq') {
    isMobileMenuOpen.value = false;
    pendingAction.value = 'addFaq';
    selectKbDialogRef.value?.open();
    return;
  }

  // 其他操作类型
  pendingAction.value = item.action || item.id;
  selectKbDialogRef.value?.open();
};

const handleSidebarToggle = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};

// 知识库选择后跳转
const handleKnowledgeBaseSelect = (kbId, actionType) => {
  isMobileMenuOpen.value = false;

  switch (actionType) {
    case 'uploadDocument':
      router.push({
        name: 'pilot_documents_file_upload',
        params: { id: kbId },
      });
      break;
    case 'crawlWechat':
      // TODO: 公众号抓取功能待开发
      router.push({
        name: 'pilot_documents_add',
        params: { knowledgeBaseId: kbId },
      });
      break;
    case 'syncMarketingCloud':
      // TODO: 同步营销云功能待开发
      router.push({
        name: 'pilot_documents_add',
        params: { knowledgeBaseId: kbId },
      });
      break;
    case 'crawlWebpage':
      router.push({
        name: 'pilot_documents_web_crawl',
        params: { id: kbId },
      });
      break;
    case 'addFaq':
      router.push({
        name: 'pilot_faqs_add',
        params: { id: kbId },
      });
      break;
    case 'importFaq':
      router.push({
        name: 'pilot_faqs_upload',
        params: { id: kbId },
      });
      break;
  }
};

// 移动端菜单切换
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

onMounted(() => {
  fetchKnowledgeBases();
});
</script>

<template>
  <section class="flex w-full h-full gap-4 p-4 overflow-hidden bg-n-surface-1">
    <!-- 桌面端侧边栏 -->
    <div v-if="!isMobile" class="hidden md:flex flex-shrink-0 h-full">
      <KnowledgeBasesSidebar
        :active-item="activeSidebarItem"
        :collapsed="sidebarCollapsed"
        class="h-full rounded-xl overflow-hidden"
        @select="handleSidebarSelect"
        @toggle-collapse="handleSidebarToggle"
      />
    </div>

    <!-- 移动端菜单遮罩 -->
    <Teleport v-if="isMobile" to="body">
      <transition
        name="fade"
        enter-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isMobileMenuOpen"
          class="fixed inset-0 z-40 bg-black/50"
          @click="toggleMobileMenu"
        />
      </transition>

      <transition
        name="slide"
        enter-active-class="transition-transform duration-200"
        enter-from-class="-translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition-transform duration-150"
        leave-from-class="translate-x-0"
        leave-to-class="-translate-x-full"
      >
        <div v-if="isMobileMenuOpen" class="fixed top-0 left-0 z-50 h-full">
          <KnowledgeBasesSidebar
            :active-item="activeSidebarItem"
            :collapsed="false"
            @select="handleSidebarSelect"
            @toggle-collapse="toggleMobileMenu"
          />
        </div>
      </transition>
    </Teleport>

    <!-- 主内容区域 -->
    <main
      class="flex-1 flex flex-col min-w-0 h-full overflow-hidden bg-n-slate-white dark:bg-n-slate-800 rounded-xl"
    >
      <!-- Header -->
      <header
        class="sticky top-0 z-10 px-4 md:px-6 bg-n-slate-white dark:bg-n-slate-800 rounded-t-xl"
      >
        <div class="w-full max-w-[60rem] mx-auto">
          <div
            class="flex items-start lg:items-center justify-between w-full py-4 lg:py-0 lg:h-20 gap-4 lg:gap-2 flex-col lg:flex-row"
          >
            <div class="flex gap-3 items-center">
              <!-- 移动端汉堡菜单 -->
              <button
                v-if="isMobile"
                type="button"
                class="p-2 -ml-2 text-n-slate-600 dark:text-n-slate-400 hover:bg-n-slate-100 dark:hover:bg-n-slate-700 rounded-lg transition-colors"
                @click="toggleMobileMenu"
              >
                <span class="i-lucide-menu text-xl" />
              </button>

              <span class="text-xl font-medium text-n-slate-12">
                {{ t('PILOT.KNOWLEDGE_BASES.HEADER') }}
              </span>
              <div class="flex items-center gap-2">
                <div class="w-0.5 h-4 rounded-2xl bg-n-weak" />
                <FeatureSpotlightPopover
                  :button-label="$t('PILOT.HEADER_KNOW_MORE')"
                  :title="
                    $t(
                      'PILOT.KNOWLEDGE_BASES.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE'
                    )
                  "
                  :note="
                    $t(
                      'PILOT.KNOWLEDGE_BASES.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE'
                    )
                  "
                  :hide-actions="!isOnChatwootCloud"
                  :fallback-thumbnail="documentPopoverLight"
                  :fallback-thumbnail-dark="documentPopoverDark"
                  learn-more-url=""
                />
              </div>
            </div>

            <div class="flex gap-2">
              <Button
                :label="$t('PILOT.KNOWLEDGE_BASES.ADD_NEW')"
                icon="i-lucide-plus"
                size="sm"
                @click="handleCreateKnowledgeBase"
              />
            </div>
          </div>

          <!-- 批量操作条和搜索筛选工具条 -->
          <div v-show="!showEmptyState">
            <!-- 批量操作条 -->
            <BulkSelectBar
              v-model="bulkSelectedIds"
              :all-items="knowledgeBasesArray"
              :select-all-label="buildSelectedCountLabel"
              :selected-count-label="selectedCountLabel"
              :delete-label="$t('PILOT.KNOWLEDGE_BASES.BULK_DELETE_BUTTON')"
              class="w-full"
            >
              <template #actions>
                <div class="flex items-center gap-2">
                  <Button
                    :label="$t('PILOT.KNOWLEDGE_BASES.BULK_PUBLISH.BUTTON')"
                    icon="i-lucide-upload-cloud"
                    size="sm"
                    ghost
                    blue
                    @click="handleBulkPublish"
                  />
                  <Button
                    :label="$t('PILOT.KNOWLEDGE_BASES.BULK_UNPUBLISH.BUTTON')"
                    icon="i-lucide-download-cloud"
                    size="sm"
                    ghost
                    blue
                    @click="handleBulkUnpublish"
                  />
                  <Button
                    :label="$t('PILOT.KNOWLEDGE_BASES.BULK_DELETE_BUTTON')"
                    icon="i-lucide-trash"
                    size="sm"
                    ghost
                    blue
                    @click="handleBulkDelete"
                  />
                </div>
              </template>
            </BulkSelectBar>

            <!-- 控制行：搜索和排序 -->
            <div v-show="bulkSelectedIds.size === 0" class="relative w-full">
              <KnowledgeBasesListControls
                key="list-controls"
                :search-value="searchValue"
                :sort-by="sortBy"
                :sort-order="sortOrder"
                :show-filter="true"
                :is-filter-open="isFilterOpen"
                @update:search-value="handleSearchChange"
                @update:sort="handleSortChange"
                @click-filter="handleToggleFilter"
              />

              <!-- 筛选弹窗 -->
              <div
                v-if="isFilterOpen"
                class="absolute top-full left-0 z-50 mt-2"
              >
                <KnowledgeBasesFilter
                  v-model="filterQuery"
                  @apply-filter="handleApplyFilter"
                  @close="handleCloseFilter"
                  @clear-filters="handleClearFilters"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      <!-- 内容区域 -->
      <div class="flex-1 px-4 md:px-6 overflow-y-auto">
        <div class="w-full max-w-[60rem] h-full mx-auto py-4">
          <!-- Loading -->
          <div
            v-if="isFetching"
            class="flex items-center justify-center py-10 text-n-slate-11"
          >
            <Spinner />
          </div>

          <!-- 空状态 -->
          <KnowledgeBasePageEmptyState
            v-else-if="showEmptyState"
            @click="handleCreateKnowledgeBase"
          />

          <!-- 搜索无结果 -->
          <div
            v-else-if="showNoResults"
            class="flex flex-col items-center justify-center py-16 text-center"
          >
            <div
              class="i-lucide-search-x text-6xl text-n-slate-7 dark:text-n-slate-4 mb-4"
            />
            <h3
              class="text-xl font-semibold text-n-slate-12 dark:text-n-slate-1 mb-2"
            >
              {{ t('PILOT.KNOWLEDGE_BASES.SEARCH.NO_RESULTS') }}
            </h3>
            <p class="text-n-slate-11 dark:text-n-slate-5">
              {{ t('PILOT.KNOWLEDGE_BASES.SEARCH.TRY_DIFFERENT_KEYWORDS') }}
            </p>
            <button
              v-if="isFiltering"
              class="mt-4 px-4 py-2 bg-n-blue-6 text-white rounded-lg hover:bg-n-blue-7 transition-colors"
              @click="handleClearFilters"
            >
              {{ t('PILOT.KNOWLEDGE_BASES.FILTER.BUTTONS.CLEAR_FILTERS') }}
            </button>
          </div>

          <!-- 卡片网格 -->
          <div
            v-else
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <KnowledgeBaseCard
              v-for="lib in knowledgeBasesArray"
              :id="lib.id"
              :key="lib.id"
              :name="lib.name"
              :description="lib.description"
              :status="lib.status"
              :documents-count="lib.documents_count"
              :webpages-count="lib.webpages_count"
              :faqs-count="lib.faqs_count"
              :assistants-count="lib.assistants_count"
              :updated-at="lib.updated_at"
              :logo-url="lib.logo_url"
              :creator="lib.creator"
              :open-menu-id="openMenuId"
              :is-selected="bulkSelectedIds.has(lib.id)"
              :selectable="hoveredCard === lib.id || bulkSelectedIds.size > 0"
              :show-menu="!bulkSelectedIds.has(lib.id)"
              @action="handleAction"
              @menu-open="handleMenuOpen"
              @menu-close="handleMenuClose"
              @select="isSelected => handleCardSelect(lib.id, isSelected)"
              @hover="isHovered => handleCardHover(isHovered, lib.id)"
            />
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <footer
        v-if="!isFetching && !showEmptyState && !!knowledgeBasesArray.length"
        class="sticky bottom-0 z-10 px-4 pb-4 mt-2"
      >
        <div class="max-w-[60rem] mx-auto">
          <PaginationFooter
            :current-page="knowledgeBasesMetaSafe.page"
            :total-items="knowledgeBasesMetaSafe.totalCount"
            :items-per-page="knowledgeBasesMetaSafe.pageSize"
            @update:current-page="onPageChange"
          />
        </div>
      </footer>
    </main>
  </section>

  <!-- 弹窗组件 -->
  <CreateKnowledgeBaseDialog
    ref="createKnowledgeBaseDialog"
    @close="handleCreateDialogClose"
    @created="handleCreateSuccess"
  />

  <EditKnowledgeBaseDialog
    v-if="selectedKnowledgeBase"
    ref="editKnowledgeBaseDialog"
    :knowledge-base="selectedKnowledgeBase"
    @updated="handleEditSuccess"
  />

  <DeleteDialog
    v-if="selectedKnowledgeBase"
    ref="deleteKnowledgeBaseDialog"
    type="KnowledgeBases"
    translation-key="KNOWLEDGE_BASES"
    :entity="selectedKnowledgeBase"
    @delete-success="onDeleteSuccess"
  />

  <BulkDeleteDialog
    ref="bulkDeleteDialog"
    :bulk-ids="Array.from(bulkSelectedIds)"
    type="KnowledgeBases"
    translation-key="KNOWLEDGE_BASES"
    @delete-success="onBulkDeleteSuccess"
  />

  <!-- 知识库选择弹窗 -->
  <SelectKnowledgeBaseDialog
    ref="selectKbDialogRef"
    :action-type="pendingAction"
    @select="handleKnowledgeBaseSelect"
    @close="pendingAction = null"
    @create-kb="handleCreateFromSelectDialog"
  />
</template>
